import Phaser from 'phaser';
import { useGameStore } from '../store/gameStore';
import { AudioManager } from '../services/AudioManager';
import { mountMusicToggleButton, unmountMusicToggleButton } from '../ui/MusicToggleButton';

interface LeaderboardEntry {
  id: string;
  nickname: string;
  score: number;
  territoryPercent: number;
  color: number;
  isLocal: boolean;
}

interface LeaderboardLayout {
  top: number;
  right: number;
  width: number;
  gap: number;
  rowHeight: number;
  rowPadding: string;
  rowGap: number;
  borderWidth: number;
  avatarSize: number;
  avatarBorder: number;
  rankSize: number;
  rankLeft: number;
  nameFontSize: number;
  percentFontSize: number;
  percentMinWidth: number;
  percentPadding: string;
  maxEntries: number;
  avatarLift: number;
}

interface HudLayout {
  top: number;
  left: number;
  width: number;
  gap: number;
  rowHeight: number;
  rowPadding: string;
  rowGap: number;
  borderWidth: number;
  iconSize: number;
  iconLift: number;
  labelFontSize: number;
  valueFontSize: number;
  valueMinWidth: number;
  valuePadding: string;
}

export class UIScene extends Phaser.Scene {
  private hudElement?: HTMLDivElement;
  private scoreValueElement?: HTMLDivElement;
  private killsValueElement?: HTMLDivElement;
  private territoryValueElement?: HTMLDivElement;
  private leaderboardElement?: HTMLDivElement;
  private minimapGraphics?: Phaser.GameObjects.Graphics;
  private minimapMask?: Phaser.GameObjects.Graphics;
  private minimapBorder?: Phaser.GameObjects.Graphics;
  private minimapSize: number = 140;
  private minimapCenterX: number = 0;
  private minimapCenterY: number = 0;
  private unsubscribeStore?: () => void;

  constructor() {
    super({ key: 'UIScene' });
  }

  create(): void {
    console.log('UIScene created');

    const isMobile = (window as any).isMobileDevice || false;
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.cleanupDomElements, this);
    this.events.once(Phaser.Scenes.Events.DESTROY, this.cleanupDomElements, this);

    // 미니맵 크기 조정
    this.minimapSize = isMobile ? 100 : 140;

    this.createHud();

    // 리더보드 생성 (우측 상단)
    this.createLeaderboard();
    mountMusicToggleButton(AudioManager.getInstance(this), 'game');

    // 원형 미니맵 (우측 하단)
    this.minimapBorder = this.add.graphics();
    this.minimapBorder.setDepth(100);

    // 미니맵 그래픽 (영역 표시용)
    this.minimapGraphics = this.add.graphics();
    this.minimapGraphics.setDepth(101);

    // 원형 마스크 생성
    this.minimapMask = this.add.graphics();
    this.minimapMask.setVisible(false);

    const mask = this.minimapMask.createGeometryMask();
    this.minimapGraphics.setMask(mask);

    // 위치/표시 여부 계산 (세로 모드에서는 숨김) + 회전 시 재배치
    this.layoutMinimap();
    this.scale.on('resize', this.layoutMinimap, this);

    // 스토어 구독
    this.unsubscribeStore = useGameStore.subscribe((state) => {
      // 씬이 활성화되어 있을 때만 업데이트
      if (this.scene.isActive()) {
        this.updateScore(state.score);
        this.updateKills(state.kills);
        this.updateTerritoryPercent(state.territoryPercent);
      }
    });

    // 초기 UI 업데이트
    const state = useGameStore.getState();
    this.updateScore(state.score);
    this.updateKills(state.kills);
    this.updateTerritoryPercent(state.territoryPercent);
  }

  shutdown() {
    // 구독 해제
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = undefined;
    }
    this.scale.off('resize', this.layoutMinimap, this);
    this.cleanupDomElements();
  }

  /**
   * 모바일 세로 화면 여부 (세로에서는 미니맵을 숨겨 조작 공간을 확보)
   */
  private isPortraitMobile(): boolean {
    const isMobile = (window as any).isMobileDevice || false;
    return isMobile && this.cameras.main.height > this.cameras.main.width;
  }

  /**
   * 미니맵 위치/표시 여부 갱신 (생성 시 + 화면 회전 시)
   */
  private layoutMinimap(): void {
    if (!this.minimapBorder || !this.minimapMask || !this.minimapGraphics) return;

    const visible = !this.isPortraitMobile();
    this.minimapBorder.setVisible(visible);
    this.minimapGraphics.setVisible(visible);
    if (!visible) {
      this.minimapGraphics.clear();
      return;
    }

    const { width, height } = this.cameras.main;
    const isMobile = (window as any).isMobileDevice || false;
    const minimapPadding = isMobile ? 10 : 20;
    const cx = width - this.minimapSize / 2 - minimapPadding;
    const cy = height - this.minimapSize / 2 - minimapPadding;
    const r = this.minimapSize / 2;
    this.minimapCenterX = cx;
    this.minimapCenterY = cy;

    // 미니맵 배경 (원형) - 입체감 있는 디자인
    this.minimapBorder.clear();
    this.minimapBorder.fillStyle(0x000000, 0.3);
    this.minimapBorder.fillCircle(cx + 4, cy + 4, r + 5);
    this.minimapBorder.fillStyle(0x2C3E50, 1);
    this.minimapBorder.fillCircle(cx, cy, r + 5);
    this.minimapBorder.fillStyle(0x1a2634, 1);
    this.minimapBorder.fillCircle(cx, cy, r + 2);
    this.minimapBorder.lineStyle(2, 0x4D96FF, 0.8);
    this.minimapBorder.strokeCircle(cx, cy, r + 5);

    // 마스크 재배치
    this.minimapMask.clear();
    this.minimapMask.fillStyle(0xffffff);
    this.minimapMask.fillCircle(cx, cy, r);
  }

  updateScore(score: number): void {
    if (this.scoreValueElement) {
      this.scoreValueElement.textContent = score.toLocaleString();
    }
  }

  updateKills(kills: number): void {
    if (this.killsValueElement) {
      this.killsValueElement.textContent = kills.toLocaleString();
    }
  }

  updateTerritoryPercent(percent: number): void {
    if (this.territoryValueElement) {
      this.territoryValueElement.textContent = `${percent.toFixed(1)}%`;
    }
  }

  private createHud(): void {
    this.cleanupHud();
    const layout = this.getHudLayout();

    this.hudElement = document.createElement('div');
    this.hudElement.id = 'game-hud';
    this.hudElement.style.cssText = `
      position: fixed;
      top: ${layout.top}px;
      left: ${layout.left}px;
      z-index: 90;
      display: flex;
      flex-direction: column;
      gap: ${layout.gap}px;
      width: ${layout.width}px;
      color: #173D75;
      font-family: Nunito, sans-serif;
      font-weight: 900;
      line-height: 1;
      pointer-events: none;
      filter: drop-shadow(0 4px 0 rgba(45, 105, 180, 0.16));
      -webkit-font-smoothing: antialiased;
      text-rendering: geometricPrecision;
    `;

    this.scoreValueElement = document.createElement('div');
    this.killsValueElement = document.createElement('div');
    this.territoryValueElement = document.createElement('div');

    const scoreChip = this.createHudChip('score', 'SCORE', this.scoreValueElement, layout);
    const killsChip = this.createHudChip('danger', 'KILLS', this.killsValueElement, layout);
    const territoryChip = this.createHudChip('territory', 'LAND', this.territoryValueElement, layout);

    this.hudElement.append(scoreChip, killsChip, territoryChip);
    document.body.appendChild(this.hudElement);
  }

  private createHudChip(
    tone: 'score' | 'danger' | 'territory',
    label: string,
    valueElement: HTMLDivElement,
    layout: HudLayout
  ): HTMLDivElement {
    const chip = document.createElement('div');
    const toneStyle = this.getHudToneStyle(tone);
    chip.style.cssText = `
      height: ${layout.rowHeight}px;
      display: grid;
      grid-template-columns: ${layout.iconSize}px minmax(0, 1fr);
      align-items: center;
      gap: ${layout.rowGap}px;
      padding: ${layout.rowPadding};
      border: ${layout.borderWidth}px solid #FFFFFF;
      border-radius: ${Math.round(layout.rowHeight / 2)}px;
      background: #EAFBFF;
      box-shadow: 0 ${layout.rowHeight <= 30 ? 3 : 4}px 0 rgba(64, 129, 216, 0.2);
      box-sizing: border-box;
    `;

    const iconElement = document.createElement('div');
    iconElement.innerHTML = this.getHudIconSvg(tone);
    iconElement.style.cssText = `
      width: ${layout.iconSize}px;
      height: ${layout.iconSize}px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: ${toneStyle.background};
      color: ${toneStyle.color};
      line-height: 0;
      overflow: hidden;
      transform: translateY(${layout.iconLift}px);
      box-shadow: 0 2px 0 rgba(33, 93, 170, 0.14);
    `;

    const textWrap = document.createElement('div');
    textWrap.style.cssText = `
      min-width: 0;
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: ${layout.rowGap}px;
    `;

    const labelElement = document.createElement('div');
    labelElement.textContent = label;
    labelElement.style.cssText = `
      color: #3A74C9;
      font-size: ${layout.labelFontSize}px;
      font-weight: 900;
      letter-spacing: 0;
      opacity: 0.92;
    `;

    valueElement.style.cssText = `
      min-width: ${layout.valueMinWidth}px;
      padding: ${layout.valuePadding};
      border-radius: 999px;
      background: #FFFFFF;
      color: #173D75;
      font-size: ${layout.valueFontSize}px;
      font-weight: 900;
      line-height: 1;
      white-space: nowrap;
      text-align: center;
      text-shadow: 0 1px 0 rgba(255,255,255,0.72);
      box-shadow: 0 1px 0 rgba(33, 93, 170, 0.1);
    `;

    textWrap.append(labelElement, valueElement);
    chip.append(iconElement, textWrap);
    return chip;
  }

  private getHudToneStyle(tone: 'score' | 'danger' | 'territory'): { background: string; color: string } {
    switch (tone) {
      case 'score':
        return {
          background: '#FFD85E',
          color: '#8B5800',
        };
      case 'danger':
        return {
          background: '#4D96FF',
          color: '#FFFFFF',
        };
      case 'territory':
        return {
          background: '#26D9D0',
          color: '#064985',
        };
    }
  }

  private getHudIconSvg(tone: 'score' | 'danger' | 'territory'): string {
    const size = '62%';
    const common = `
      width="${size}"
      height="${size}"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      style="display:block;flex:0 0 auto;"
    `;

    switch (tone) {
      case 'score':
        return `
          <svg ${common}>
            <path fill="currentColor" d="M12 2.4l2.55 6.35L21 12l-6.45 3.25L12 21.6l-2.55-6.35L3 12l6.45-3.25L12 2.4z"/>
          </svg>
        `;
      case 'danger':
        return `
          <svg ${common}>
            <path fill="currentColor" d="M12 3.1c-4.25 0-7.35 2.95-7.35 6.95 0 2.35 1.15 4.45 3.05 5.8v2.2c0 .7.55 1.25 1.25 1.25h6.1c.7 0 1.25-.55 1.25-1.25v-2.2c1.9-1.35 3.05-3.45 3.05-5.8 0-4-3.1-6.95-7.35-6.95zM9.25 11.25a1.65 1.65 0 1 1 0-3.3 1.65 1.65 0 0 1 0 3.3zm5.5 0a1.65 1.65 0 1 1 0-3.3 1.65 1.65 0 0 1 0 3.3zM12 11.9l1.55 2.65h-3.1L12 11.9zm-2.2 4.3h1.15v1.8H9.8v-1.8zm3.25 0h1.15v1.8h-1.15v-1.8z"/>
          </svg>
        `;
      case 'territory':
        return `
          <svg ${common}>
            <path fill="currentColor" d="M12 2.8l8.05 4.65v9.1L12 21.2l-8.05-4.65v-9.1L12 2.8z"/>
          </svg>
        `;
    }
  }

  /**
   * 리더보드 UI 생성
   */
  private createLeaderboard(): void {
    const layout = this.getLeaderboardLayout();
    this.cleanupLeaderboard();

    this.leaderboardElement = document.createElement('div');
    this.leaderboardElement.id = 'game-leaderboard';
    this.leaderboardElement.style.cssText = `
      position: fixed;
      top: ${layout.top}px;
      right: ${layout.right}px;
      z-index: 90;
      width: ${layout.width}px;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: ${layout.gap}px;
      color: #FFFFFF;
      font-family: Nunito, sans-serif;
      pointer-events: none;
      box-sizing: border-box;
      filter: drop-shadow(0 4px 0 rgba(29, 49, 112, 0.16));
      -webkit-font-smoothing: antialiased;
      text-rendering: geometricPrecision;
    `;
    document.body.appendChild(this.leaderboardElement);
  }

  /**
   * 리더보드 업데이트 - 심플한 디자인
   */
  updateLeaderboard(entries: LeaderboardEntry[]): void {
    if (!this.leaderboardElement) return;

    const layout = this.getLeaderboardLayout();
    this.leaderboardElement.style.top = `${layout.top}px`;
    this.leaderboardElement.style.right = `${layout.right}px`;
    this.leaderboardElement.style.width = `${layout.width}px`;
    this.leaderboardElement.style.gap = `${layout.gap}px`;
    this.leaderboardElement.style.alignItems = 'flex-end';

    const maxEntries = Math.min(entries.length, layout.maxEntries);

    // 영역 점유율 기준 정렬 (내림차순)
    const sorted = [...entries].sort((a, b) => b.territoryPercent - a.territoryPercent);
    const rows: string[] = [];

    // 엔트리 생성
    for (let i = 0; i < maxEntries; i++) {
      const entry = sorted[i];

      // 순위
      const rankDisplay = `${i + 1}`;
      const rankStyle = this.getRankStyle(i);

      const displayName = entry.nickname;
      const colorHex = this.colorToHex(entry.color);
      const avatarTextColor = this.getReadableTextColor(entry.color);
      const rowBackground = entry.isLocal ? '#FFF6D7' : '#EAFBFF';
      const rowShadow = entry.isLocal
        ? 'box-shadow: 0 0 0 2px rgba(255,216,94,0.85), 0 4px 0 rgba(64,129,216,0.2);'
        : 'box-shadow: 0 3px 0 rgba(64,129,216,0.18);';
      const rowWidth = this.getLeaderboardRowWidth(layout, i);
      const avatar = this.getAvatarText(entry.nickname, i);
      rows.push(`
        <div style="
          position: relative;
          width: ${rowWidth}px;
          height: ${layout.rowHeight}px;
          display: grid;
          grid-template-columns: ${layout.avatarSize}px minmax(0, 1fr) auto;
          align-items: center;
          gap: ${layout.rowGap}px;
          padding: ${layout.rowPadding};
          border: ${layout.borderWidth}px solid #FFFFFF;
          border-radius: ${Math.round(layout.rowHeight / 2)}px;
          background: ${rowBackground};
          ${rowShadow}
          box-sizing: border-box;
        ">
          <div style="
            position: absolute;
            left: ${layout.rankLeft}px;
            top: 50%;
            width: ${layout.rankSize}px;
            height: ${layout.rankSize}px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: ${rankStyle.color};
            font-size: ${Math.max(8, layout.rankSize - 9)}px;
            font-weight: 900;
            line-height: 1;
            border-radius: 50%;
            border: 2px solid #FFFFFF;
            background: ${rankStyle.background};
            box-shadow: 0 2px 0 rgba(18,54,126,0.16);
            transform: translateY(-50%);
          ">${rankDisplay}</div>
          <div style="
            width: ${layout.avatarSize}px;
            height: ${layout.avatarSize}px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            border: ${layout.avatarBorder}px solid #FFFFFF;
            background: ${colorHex};
            color: ${avatarTextColor};
            font-size: ${Math.max(10, layout.avatarSize - 15)}px;
            font-weight: ${entry.isLocal ? 900 : 800};
            line-height: 1;
            box-shadow: 0 2px 0 rgba(33,62,124,0.14);
            transform: translateY(${layout.avatarLift}px);
          ">${this.escapeHtml(avatar)}</div>
          <div style="
            min-width: 0;
            color: #173D75;
            font-size: ${layout.nameFontSize}px;
            font-weight: ${entry.isLocal ? 900 : 800};
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            text-shadow: 0 1px 0 rgba(255,255,255,0.5);
          ">${this.escapeHtml(displayName)}</div>
          <div style="
            min-width: ${layout.percentMinWidth}px;
            padding: ${layout.percentPadding};
            border-radius: 999px;
            background: #FFFFFF;
            color: #2261B3;
            font-size: ${layout.percentFontSize}px;
            font-weight: 900;
            white-space: nowrap;
            text-align: center;
            box-shadow: 0 1px 0 rgba(33,62,124,0.12);
          ">${entry.territoryPercent.toFixed(1)}%</div>
        </div>
      `);
    }

    this.leaderboardElement.innerHTML = rows.join('');
  }

  private getHudLayout(): HudLayout {
    const viewportWidth = window.innerWidth || this.scale.width || 1280;
    const viewportHeight = window.innerHeight || this.scale.height || 720;
    const isDeviceMobile = (window as any).isMobileDevice || false;
    const isTiny = viewportWidth <= 420;
    const isCompact = isTiny || isDeviceMobile || viewportWidth <= 640 || viewportHeight <= 500;

    if (isTiny) {
      return {
        top: 8,
        left: 8,
        width: Math.round(this.clamp(viewportWidth * 0.34, 126, 138)),
        gap: 4,
        rowHeight: 28,
        rowPadding: '3px 5px 3px 4px',
        rowGap: 5,
        borderWidth: 2,
        iconSize: 22,
        iconLift: -1,
        labelFontSize: 8,
        valueFontSize: 12,
        valueMinWidth: 38,
        valuePadding: '2px 5px',
      };
    }

    if (isCompact) {
      return {
        top: 10,
        left: 10,
        width: Math.round(this.clamp(viewportWidth * 0.3, 140, 164)),
        gap: 5,
        rowHeight: 31,
        rowPadding: '4px 6px 4px 5px',
        rowGap: 6,
        borderWidth: 2,
        iconSize: 24,
        iconLift: -1,
        labelFontSize: 8,
        valueFontSize: 13,
        valueMinWidth: 42,
        valuePadding: '3px 6px',
      };
    }

    return {
      top: 15,
      left: 15,
      width: 178,
      gap: 7,
      rowHeight: 38,
      rowPadding: '5px 9px 5px 6px',
      rowGap: 8,
      borderWidth: 3,
      iconSize: 30,
      iconLift: -2,
      labelFontSize: 9,
      valueFontSize: 15,
      valueMinWidth: 48,
      valuePadding: '4px 8px',
    };
  }

  private getLeaderboardLayout(): LeaderboardLayout {
    const viewportWidth = window.innerWidth || this.scale.width || 1280;
    const viewportHeight = window.innerHeight || this.scale.height || 720;
    const isDeviceMobile = (window as any).isMobileDevice || false;
    const isTiny = viewportWidth <= 420;
    const isCompact = isTiny || isDeviceMobile || viewportWidth <= 640 || viewportHeight <= 500;

    if (isTiny) {
      const width = Math.round(this.clamp(viewportWidth * 0.34, 122, 138));
      return {
        top: 8,
        right: 10,
        width,
        gap: 4,
        rowHeight: 26,
        rowPadding: '3px 5px 3px 4px',
        rowGap: 4,
        borderWidth: 2,
        avatarSize: 20,
        avatarBorder: 2,
        rankSize: 15,
        rankLeft: -6,
        nameFontSize: 10,
        percentFontSize: 10,
        percentMinWidth: 38,
        percentPadding: '2px 5px',
        maxEntries: 5,
        avatarLift: -2,
      };
    }

    if (isCompact) {
      const width = Math.round(this.clamp(viewportWidth * 0.28, 140, 176));
      return {
        top: 10,
        right: 14,
        width,
        gap: 5,
        rowHeight: 30,
        rowPadding: '4px 6px 4px 5px',
        rowGap: 5,
        borderWidth: 2,
        avatarSize: 24,
        avatarBorder: 2,
        rankSize: 17,
        rankLeft: -7,
        nameFontSize: 11,
        percentFontSize: 11,
        percentMinWidth: 42,
        percentPadding: '3px 6px',
        maxEntries: 6,
        avatarLift: -2,
      };
    }

    return {
      top: 15,
      right: 28,
      width: 218,
      gap: 7,
      rowHeight: 38,
      rowPadding: '5px 9px 5px 7px',
      rowGap: 8,
      borderWidth: 3,
      avatarSize: 30,
      avatarBorder: 3,
      rankSize: 19,
      rankLeft: -8,
      nameFontSize: 14,
      percentFontSize: 14,
      percentMinWidth: 56,
      percentPadding: '4px 8px',
      maxEntries: 8,
      avatarLift: -3,
    };
  }

  private getLeaderboardRowWidth(layout: LeaderboardLayout, rankIndex: number): number {
    const step = layout.width <= 140 ? 5 : layout.width <= 176 ? 7 : 8;
    const contentMinWidth =
      layout.avatarSize +
      layout.percentMinWidth +
      layout.rowGap * 2 +
      layout.rankSize +
      layout.borderWidth * 2 +
      18;
    const visualMinWidth = Math.round(layout.width * (layout.width <= 140 ? 0.84 : 0.76));
    const minWidth = Math.min(layout.width, Math.max(contentMinWidth, visualMinWidth));

    return Math.max(minWidth, layout.width - rankIndex * step);
  }

  /**
   * 원형 미니맵 업데이트 (그리드 기반)
   */
  updateCircularMinimap(
    territories: { playerId: string; color: number; points: { x: number; y: number }[] }[],
    playerX: number,
    playerY: number,
    worldCenterX: number,
    worldCenterY: number,
    worldRadius: number
  ): void {
    if (!this.minimapGraphics) return;
    // 세로 모드(모바일)에서는 미니맵 숨김 상태이므로 그리지 않는다
    if (!this.minimapGraphics.visible) return;

    const minimapCenterX = this.minimapCenterX;
    const minimapCenterY = this.minimapCenterY;
    const minimapRadius = this.minimapSize / 2;

    const scale = minimapRadius / worldRadius;
    const cellSize = 2; // 미니맵에서의 셀 크기

    this.minimapGraphics.clear();

    // 월드 배경
    this.minimapGraphics.fillStyle(0x3D5A73, 1);
    this.minimapGraphics.fillCircle(minimapCenterX, minimapCenterY, minimapRadius);

    // 영역 그리기 (각 셀을 점으로)
    territories.forEach((territory) => {
      if (territory.points.length === 0) return;

      const brightColor = this.toBrightColor(territory.color);
      this.minimapGraphics!.fillStyle(brightColor, 1);

      for (const point of territory.points) {
        const mx = minimapCenterX + (point.x - worldCenterX) * scale;
        const my = minimapCenterY + (point.y - worldCenterY) * scale;

        // 미니맵 원형 범위 내인지 확인
        const distFromCenter = Math.sqrt(
          (mx - minimapCenterX) ** 2 + (my - minimapCenterY) ** 2
        );
        if (distFromCenter <= minimapRadius) {
          this.minimapGraphics!.fillRect(mx - cellSize / 2, my - cellSize / 2, cellSize, cellSize);
        }
      }
    });

    // 플레이어 위치 표시 (밝은 점)
    const playerMX = minimapCenterX + (playerX - worldCenterX) * scale;
    const playerMY = minimapCenterY + (playerY - worldCenterY) * scale;

    this.minimapGraphics.fillStyle(0xFFFFFF, 1);
    this.minimapGraphics.fillCircle(playerMX, playerMY, 4);
    this.minimapGraphics.lineStyle(2, 0x000000, 0.5);
    this.minimapGraphics.strokeCircle(playerMX, playerMY, 4);
  }

  /**
   * 밝은 색상으로 변환
   */
  private toBrightColor(color: number): number {
    const r = (color >> 16) & 0xFF;
    const g = (color >> 8) & 0xFF;
    const b = color & 0xFF;

    const brightR = Math.min(255, Math.floor(r + (255 - r) * 0.2));
    const brightG = Math.min(255, Math.floor(g + (255 - g) * 0.2));
    const brightB = Math.min(255, Math.floor(b + (255 - b) * 0.2));

    return (brightR << 16) | (brightG << 8) | brightB;
  }

  updateMinimap(_tiles: { x: number; y: number; owner_id: string | null; color?: number }[]): void {
    // 기존 호환성 유지
  }

  updatePlayerPosition(_x: number, _y: number): void {
    // 기존 호환성 유지
  }

  private cleanupHud(): void {
    const existingHud = document.getElementById('game-hud');
    if (existingHud) existingHud.remove();
    this.hudElement?.remove();
    this.hudElement = undefined;
    this.scoreValueElement = undefined;
    this.killsValueElement = undefined;
    this.territoryValueElement = undefined;
  }

  private cleanupLeaderboard(): void {
    const existingLeaderboard = document.getElementById('game-leaderboard');
    if (existingLeaderboard) existingLeaderboard.remove();
    this.leaderboardElement?.remove();
    this.leaderboardElement = undefined;
  }

  private cleanupDomElements(): void {
    this.cleanupHud();
    this.cleanupLeaderboard();
    unmountMusicToggleButton();
  }

  private colorToHex(color: number): string {
    return `#${color.toString(16).padStart(6, '0')}`;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  private getReadableTextColor(color: number): string {
    const r = (color >> 16) & 0xFF;
    const g = (color >> 8) & 0xFF;
    const b = color & 0xFF;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.72 ? '#17406F' : '#FFFFFF';
  }

  private getRankStyle(index: number): { background: string; color: string } {
    if (index === 0) return { background: '#FFD85E', color: '#8B5800' };
    if (index === 1) return { background: '#F4F8FF', color: '#3E6696' };
    if (index === 2) return { background: '#FFB066', color: '#7F3A00' };
    return { background: '#DCEEFF', color: '#2865A6' };
  }

  private getAvatarText(nickname: string, index: number): string {
    if (index === 0) return '♛';
    const first = nickname.trim().charAt(0);
    return first || '●';
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
